import React, { useEffect } from 'react';
import { X, Facebook, Instagram, Youtube, ExternalLink } from 'lucide-react';
import SectionTitle from './SectionTitle';

const SocialMediaSection = () => {
  // Load social media scripts when component mounts
  useEffect(() => {
    // Load Facebook SDK
    if (window.FB) {
      window.FB.XFBML.parse();
    } else {
      window.fbAsyncInit = function() {
        window.FB.init({
          appId: 'your-facebook-app-id', // Replace with your Facebook App ID
          cookie: true,
          xfbml: true,
          version: 'v18.0'
        });
      };
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    // Load Instagram embed script
    if (!window.instgrm) {
      const script = document.createElement('script');
      script.src = 'https://www.instagram.com/embed.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  return (
    <section id="social" className="w-full bg-gradient-to-br from-gray-50 to-orange-50 py-20 px-4 md:px-16">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <SectionTitle>सोशल मीडिया</SectionTitle>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
            हमारे सोशल मीडिया प्लेटफॉर्म्स पर जुड़ें और नवीनतम अपडेट्स प्राप्त करें
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-600 to-red-600 mx-auto mt-6 rounded-full"></div>
        </div>

        {/* Social Media Grid - All platforms at once */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          
          {/* Facebook */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Facebook className="w-8 h-8 text-white" />
                  <div>
                    <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                      फेसबुक
                    </h3>
                    <p className="text-blue-100 text-sm">Facebook Page</p>
                  </div>
                </div>
                <a 
                  href="https://facebook.com/sidhisochsidhibaat" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-blue-200 transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="p-4 h-80 overflow-hidden">
              {/* Placeholder image for Facebook */}
              <div className="w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Facebook className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                  <p className="text-blue-600 font-semibold" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                    फेसबुक पेज
                  </p>
                  <p className="text-blue-500 text-sm mt-2" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                    नवीनतम पोस्ट्स देखें
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* X (formerly Twitter) */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
            <div className="bg-gradient-to-r from-gray-900 to-black p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <X className="w-8 h-8 text-white" />
                  <div>
                    <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                      X (ट्विटर)
                    </h3>
                    <p className="text-gray-300 text-sm">X Profile</p>
                  </div>
                </div>
                <a 
                  href="https://x.com/sidhisochsidhibaat" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-gray-300 transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="p-4 h-80 overflow-hidden">
              {/* Placeholder image for X */}
              <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <X className="w-16 h-16 text-gray-800 mx-auto mb-4" />
                  <p className="text-gray-800 font-semibold" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                    X प्रोफाइल
                  </p>
                  <p className="text-gray-600 text-sm mt-2" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                    नवीनतम ट्वीट्स देखें
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Instagram */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Instagram className="w-8 h-8 text-white" />
                  <div>
                    <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                      इंस्टाग्राम
                    </h3>
                    <p className="text-pink-100 text-sm">Instagram Profile</p>
                  </div>
                </div>
                <a 
                  href="https://instagram.com/sidhisochsidhibaat" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-pink-200 transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="p-4 h-80 overflow-hidden">
              {/* Placeholder image for Instagram */}
              <div className="w-full h-full bg-gradient-to-br from-pink-50 to-purple-100 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Instagram className="w-16 h-16 text-pink-600 mx-auto mb-4" />
                  <p className="text-pink-600 font-semibold" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                    इंस्टाग्राम प्रोफाइल
                  </p>
                  <p className="text-pink-500 text-sm mt-2" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                    फोटोज़ और स्टोरीज़ देखें
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* YouTube */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Youtube className="w-8 h-8 text-white" />
                  <div>
                    <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                      यूट्यूब
                    </h3>
                    <p className="text-red-100 text-sm">YouTube Channel</p>
                  </div>
                </div>
                <a 
                  href="https://youtube.com/@sidhisochsidhibaat" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white hover:text-red-200 transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="p-4 h-80 overflow-hidden">
              {/* Placeholder image for YouTube */}
              <div className="w-full h-full bg-gradient-to-br from-red-50 to-red-100 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Youtube className="w-16 h-16 text-red-600 mx-auto mb-4" />
                  <p className="text-red-600 font-semibold" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                    यूट्यूब चैनल
                  </p>
                  <p className="text-red-500 text-sm mt-2" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
                    वीडियो और प्लेलिस्ट देखें
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action Buttons */}
        {/* <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-8" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
            हमसे जुड़ें
          </h3>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            <a
              href="https://facebook.com/sidhisochsidhibaat"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}
            >
              <Facebook className="w-5 h-5" />
              फेसबुक पर फॉलो करें
            </a>
            <a
              href="https://x.com/sidhisochsidhibaat"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-black transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}
            >
              <X className="w-5 h-5" />
              X पर फॉलो करें
            </a>
            <a
              href="https://instagram.com/sidhisochsidhibaat"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold hover:from-pink-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}
            >
              <Instagram className="w-5 h-5" />
              इंस्टाग्राम पर फॉलो करें
            </a>
            <a
              href="https://youtube.com/@sidhisochsidhibaat"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-red-700 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}
            >
              <Youtube className="w-5 h-5" />
              यूट्यूब पर सब्सक्राइब करें
            </a>
          </div>
          <p className="text-gray-600 mt-6" style={{ fontFamily: 'Noto Sans Devanagari, Arial, sans-serif' }}>
            सभी प्लेटफॉर्म्स पर नवीनतम अपडेट्स के लिए हमें फॉलो करें
          </p>
        </div> */}
      </div>
    </section>
  );
};

export default SocialMediaSection;